import {useSelector} from 'react-redux';

import Image from '@/components/Image/Image';
import ButtonRaw from '@/components/Buttons/ButtonRaw';
import useExternalLinks from '@/hooks/useExternalLinks';
import {formatPlural} from '@/services/plural';
import {selectLanguageCode} from '@/state/selectors/app';
import type {ExternalLinkExtendedModel, PluralFormsModel} from '@/types/aem';
import type {BadgeReward} from '@/types/loyalty';
import {formatBadgeImageUrl} from '@/utils/journeysUtils';

type BadgeRewardsListProps = {
    rewards: BadgeReward[];
    journeyId?: string;
    selectRewardPluralForms?: PluralFormsModel | null;
    rewardDetailExternalLink?: ExternalLinkExtendedModel | null;
};

type BadgeRewardCardProps = {
    reward: BadgeReward;
    journeyId?: string;
    rewardDetailExternalLink?: ExternalLinkExtendedModel | null;
};

const BadgeRewardCard = ({reward, journeyId, rewardDetailExternalLink}: BadgeRewardCardProps) => {
    const src = reward.imageUrl ? formatBadgeImageUrl(reward.imageUrl, 88, 88) : undefined;
    const redirect = useExternalLinks({externalLink: rewardDetailExternalLink, params: {journeyId, rewardId: reward.id}});

    return (
        <ButtonRaw
            onClick={() => redirect?.()}
            disabled={!redirect}
            className='flex w-full items-stretch overflow-hidden border-0 bg-graphite p-0 text-left first:rounded-t-xl last:rounded-b-xl disabled:cursor-default'
        >
            {src && <Image src={src} alt='' aria-hidden className='h-22 w-22 shrink-0 object-cover' />}
            <div className='flex flex-1 items-center px-4 py-4'>
                {reward.name && <span className='line-clamp-2 text-16 leading-6 text-romance'>{reward.name}</span>}
            </div>
        </ButtonRaw>
    );
};

export const BadgeRewardsList = ({rewards, journeyId, selectRewardPluralForms, rewardDetailExternalLink}: BadgeRewardsListProps) => {
    const languageCode = useSelector(selectLanguageCode);

    if (rewards.length === 0) return null;

    const title = formatPlural(rewards.length, selectRewardPluralForms, languageCode);

    return (
        <div className='flex flex-col gap-4 px-4'>
            {title && <h2 className='m-0 font-bold text-20 leading-7 tracking-02 text-romance'>{title}</h2>}
            <div className='flex flex-col gap-1'>
                {rewards.map((reward, index) => (
                    <BadgeRewardCard
                        key={`${reward.id}-${index}`}
                        reward={reward}
                        journeyId={journeyId}
                        rewardDetailExternalLink={rewardDetailExternalLink}
                    />
                ))}
            </div>
        </div>
    );
};
